import { AxiosHeaders } from 'axios';
import { authenticatedRequest } from '@/services';
import { HttpMethod } from '@/lib/enums';
import type {
  AudienceDefinition,
  AudienceMetrics,
  SavedAudience,
} from '../lib/audience-definition';

/**
 * The saved-audience API (NPJB-711 / 722 / 723).
 *
 * One module rather than a file per verb: these five calls are one resource and change
 * together, and splitting them would mean five files importing the same two types.
 *
 * **Only `create` may send `X-Tenant-Id`, and only from a client picker.** The audience
 * page has one ("Creating on behalf of"): an internal user's new audience belongs to the
 * picked client — its tenant and its ICP. A platform user sends nothing, and the backend
 * files the row under their own ICP. Update and archive are addressed by the row's own id,
 * which needs no tenant. The rule is in `src/app/CLAUDE.md`
 * § "Tenant Scoping".
 */

const RESOURCE = 'icp/audiences';

export interface SavedAudienceListResponse {
  items: SavedAudience[];
}

export const fetchSavedAudiences = (): Promise<SavedAudienceListResponse> =>
  authenticatedRequest({
    resource: RESOURCE,
    method: HttpMethod.GET,
  }) as Promise<SavedAudienceListResponse>;

export interface CreateSavedAudienceArgs {
  title: string;
  filters: AudienceDefinition;
  /** The KPI strip's counts at the moment Save was pressed (NPJB-723 AC3). */
  metrics: AudienceMetrics;
  /**
   * The client an internal user creates for, from a client picker. Omitted for a platform
   * user, and wherever there is no picker.
   */
  tenantId?: string;
}

export const createSavedAudience = ({
  title,
  filters,
  metrics,
  tenantId,
}: CreateSavedAudienceArgs): Promise<SavedAudience> => {
  const headers = new AxiosHeaders();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  return authenticatedRequest({
    resource: RESOURCE,
    method: HttpMethod.POST,
    data: { title, filters, metrics },
    headers,
  }) as Promise<SavedAudience>;
};

export interface UpdateSavedAudienceArgs {
  id: string;
  /** Omit to leave the title alone (NPJB-722 AC8 sends only filters). */
  title?: string;
  /** Omit to leave the filters alone (NPJB-723 AC7 sends only a title). */
  filters?: AudienceDefinition;
  /** Travels with `filters`: new filters mean a new size. A rename sends neither. */
  metrics?: AudienceMetrics;
}

export const updateSavedAudience = ({
  id,
  title,
  filters,
  metrics,
}: UpdateSavedAudienceArgs): Promise<SavedAudience> =>
  authenticatedRequest({
    resource: `${RESOURCE}/${id}`,
    method: HttpMethod.PATCH,
    data: {
      ...(title === undefined ? {} : { title }),
      ...(filters === undefined ? {} : { filters }),
      ...(metrics === undefined ? {} : { metrics }),
    },
  }) as Promise<SavedAudience>;

export const archiveSavedAudience = (id: string): Promise<SavedAudience> =>
  authenticatedRequest({
    resource: `${RESOURCE}/${id}/archive`,
    method: HttpMethod.POST,
  }) as Promise<SavedAudience>;
